"use client";

import { Copy, Lock, RefreshCw, Sparkles } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { toast } from "sonner";

import { extractApiErrorMessage } from "@/shared/api/error-handler";
import { formatTimeOnlyMsk } from "@/shared/lib/msk-time";
import { Badge } from "@/shared/ui/data-display/badge";
import { Skeleton } from "@/shared/ui/data-display/skeleton";
import { Button } from "@/shared/ui/buttons/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/modals/dialog";

import {
  useDdragonChampionNames,
  useMatchChampionPools,
  usePublishChampionPool,
  type ChampionPoolGame,
} from "../model/use-match-champion-pools";
import { ChampionPoolGrid } from "./champion-pool-grid";

export interface MatchChampionPoolsSectionProps {
  tournamentId: string;
  matchId: string;
  bestOf: number;
  /** Гранд-финал (DE): в интерфейсе подсвечиваем «пул 10». Размер всё равно решает бэкенд. */
  isFinal?: boolean;
  /** Организатор/staff турнира: видит кнопку выдачи пула. */
  canPublish?: boolean;
  /** Матч завершён: пулы больше не меняются — опрос отключаем. */
  matchCompleted?: boolean;
  className?: string;
}

/**
 * KAN-1676: пулы чемпионов RandomDraft на странице матча.
 *
 * Одно место на всю фичу, без модалок: игры серии — табами внутри блока,
 * обе стороны — двумя колонками. Публикация — единственный диалог (действие
 * необратимо). Пустой блок у обычных турниров не рисуется: если пулов нет и
 * смотрящий не staff, секция возвращает null, а не пустую карточку.
 */
export function MatchChampionPoolsSection({
  tournamentId,
  matchId,
  bestOf,
  isFinal = false,
  canPublish = false,
  matchCompleted = false,
  className,
}: MatchChampionPoolsSectionProps) {
  const t = useTranslations("Match");
  const championNames = useDdragonChampionNames();
  const { games, isLoading, isError, refetch } = useMatchChampionPools(matchId, {
    // Пока матч не завершён, пул может появиться в любой момент: организатор
    // выдаёт его из другого окна, зритель должен увидеть без перезагрузки.
    refetchInterval: matchCompleted ? 0 : 30_000,
  });
  const { publish, isPublishing } = usePublishChampionPool(matchId, tournamentId);

  const [selectedGame, setSelectedGame] = useState(1);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const autoSelectedRef = useRef(false);

  const publishedNumbers = useMemo(
    () => new Set(games.map((game) => game.gameNumber)),
    [games],
  );

  // Автовыбор один раз после загрузки: первая невыданная игра (для staff это
  // «следующая к выдаче», для зрителя — текущая). Дальше выбор пользователя
  // не перебиваем, иначе табы прыгали бы после каждого рефетча.
  useEffect(() => {
    if (autoSelectedRef.current || isLoading) return;
    if (games.length === 0 && !canPublish) return;

    const published = [...publishedNumbers].sort((a, b) => a - b);
    let next: number;
    if (canPublish) {
      // Организатору — первая невыданная игра: от неё он и жмёт «выдать».
      next = Math.max(1, bestOf);
      for (let game = 1; game <= Math.max(1, bestOf); game += 1) {
        if (!publishedNumbers.has(game)) {
          next = game;
          break;
        }
      }
    } else {
      // Зрителю — последняя выданная игра (фактически текущая), а не следующая
      // пустая: у завершённого 2:0 матча игра 3 никогда не игралась.
      next = published.length > 0 ? published[published.length - 1]! : 1;
    }
    autoSelectedRef.current = true;
    setSelectedGame(next);
  }, [bestOf, canPublish, games.length, isLoading, publishedNumbers]);

  const game = games.find((item) => item.gameNumber === selectedGame);

  // Обычный турнир без пулов: не засоряем страницу матча.
  if (!isLoading && !isError && games.length === 0 && !canPublish) {
    return null;
  }

  const copyPool = async () => {
    if (!game) return;
    const blue = game.pools.find((pool) => pool.side === "Blue");
    const red = game.pools.find((pool) => pool.side === "Red");
    const format = (ids: string[]) =>
      ids.map((id) => championNames.get(id) ?? id).join(", ");
    const text = [
      `${t("poolsBlue")}: ${format(blue?.championIds ?? [])}`,
      `${t("poolsRed")}: ${format(red?.championIds ?? [])}`,
    ].join("\n");

    try {
      await navigator.clipboard.writeText(text);
      toast.success(t("poolsCopied"));
    } catch {
      toast.error(t("poolsCopyError"));
    }
  };

  const handlePublish = () => {
    publish(selectedGame, undefined, {
      onSuccess: () => toast.success(t("poolsPublished")),
      onError: (error) => toast.error(extractApiErrorMessage(error, t("poolsPublishError"))),
    });
    setConfirmOpen(false);
  };

  return (
    <section id="pools" data-testid="pools-section" data-game={selectedGame} className={`scroll-mt-24 ${className ?? ""}`}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-sm font-semibold text-foreground">
            {t("poolsTitle")}
          </h2>
          {isFinal && (
            <Badge variant="outline" className="gap-1">
              <Sparkles className="size-3" aria-hidden />
              {t("poolsFinalBadge")}
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2">
          {game && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => void copyPool()}
              title={t("poolsCopy")}
            >
              <Copy className="size-4" aria-hidden />
              <span className="hidden sm:inline">{t("poolsCopy")}</span>
            </Button>
          )}
          {bestOf > 1 && (
            // Простые кнопки вместо Radix Tabs: табы — это переключатель игр,
            // отдельного контента-панели нет, а Radix здесь добавлял только
            // roving-focus и ломал клик в тестах. Стили повторяют TabsTrigger.
            <div
              role="tablist"
              aria-label={t("poolsTitle")}
              className="inline-flex h-8 items-center justify-center gap-0.5 rounded-lg bg-muted p-[3px]"
            >
              {Array.from({ length: bestOf }, (_, index) => index + 1).map(
                (gameNumber) => {
                  const active = selectedGame === gameNumber;
                  return (
                    <button
                      key={gameNumber}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setSelectedGame(gameNumber)}
                      className={`inline-flex h-[calc(100%-1px)] items-center justify-center gap-1 rounded-md px-2 text-xs font-medium whitespace-nowrap transition-colors ${
                        active
                          ? "bg-background text-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {t("poolsGameTab", { n: gameNumber })}
                      {publishedNumbers.has(gameNumber) && (
                        <span
                          className="inline-block size-1.5 rounded-full bg-primary"
                          aria-hidden
                        />
                      )}
                    </button>
                  );
                },
              )}
            </div>
          )}
        </div>
      </div>

      {isLoading && (
        <div className="grid grid-cols-2 gap-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      )}

      {!isLoading && isError && (
        <div className="flex items-center gap-3 rounded-lg border border-dashed border-border px-4 py-3 text-sm text-muted-foreground">
          <span>{t("poolsError")}</span>
          <Button type="button" variant="outline" size="sm" onClick={() => void refetch()}>
            <RefreshCw className="size-4" aria-hidden />
            {t("poolsRetry")}
          </Button>
        </div>
      )}

      {!isLoading && !isError && !game && (
        <div className="flex flex-col items-start gap-3 rounded-lg border border-dashed border-border px-4 py-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Lock className="size-4" aria-hidden />
            <span className="font-medium text-foreground">{t("poolsEmpty")}</span>
          </div>
          <p>{t("poolsEmptyHint")}</p>
          {canPublish && (
            <Button
              type="button"
              size="sm"
              onClick={() => setConfirmOpen(true)}
              disabled={isPublishing}
            >
              <Sparkles className="size-4" aria-hidden />
              {isPublishing ? t("poolsPublishing") : t("poolsPublish")}
            </Button>
          )}
        </div>
      )}

      {!isLoading && !isError && game && (
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-[1fr_auto_1fr] sm:items-start">
            <ChampionPoolGrid
              label={t("poolsBlue")}
              championNames={championNames}
              side={game.pools.find((pool) => pool.side === "Blue") ?? { side: "Blue", championIds: [] }}
            />
            <div className="hidden self-center px-2 text-xs font-medium uppercase tracking-widest text-muted-foreground sm:block">
              vs
            </div>
            <ChampionPoolGrid
              label={t("poolsRed")}
              championNames={championNames}
              side={game.pools.find((pool) => pool.side === "Red") ?? { side: "Red", championIds: [] }}
            />
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span>{t("poolsHint")}</span>
            <span aria-hidden>·</span>
            <span>
              {t("poolsPublishedAt", {
                time: formatTimeOnlyMsk(game.createdAt, { hour: "2-digit", minute: "2-digit" }),
              })}
            </span>
          </div>
        </div>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("poolsPublishConfirmTitle", {
                n: selectedGame,
                size: isFinal ? 10 : 6,
              })}
            </DialogTitle>
            <DialogDescription>{t("poolsPublishConfirmText")}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirmOpen(false)}>
              {t("poolsPublishCancel")}
            </Button>
            <Button type="button" onClick={handlePublish} disabled={isPublishing}>
              {isPublishing ? t("poolsPublishing") : t("poolsPublishConfirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

/** Внутренний тип для тестов и внешних вызовов (не экспортируется в бандл). */
export type { ChampionPoolGame };
